/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useEffect } from 'react';
import { Box, Button, IconButton, Table, TableBody, TableHead, TableRow } from '@mui/material';
import { WarningRounded } from '@mui/icons-material';
import { Feature } from '@turf/helpers';
import isNil from 'lodash/isNil';
import size from 'lodash/size';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { withTheme } from 'styled-components';
import MissionPlan from '../../../../../../shared/entities/missionPlan';
import { InstallationType } from '../../../../../../shared/entities/terminal';
import {
  selectFlightLegServiceAlerts,
  selectLoadingMissionPlans,
  selectMissionOverlayMissionsSortDirection,
  selectMissionOverlayMissionsSortField,
  selectMissionPlans,
  selectMissionPlansError,
  selectSelectedOverlaidFlightLegId,
  selectselectedOverlaidMissionPlanId,
  selectTerminalDetails,
  selectTerminalType,
  selectUserId,
} from '../../../../../../store';
import { getFlightLegs } from '../../../../../../store/flightLegs/actions';
import { getMissionPlans } from '../../../../../../store/missionPlans/actions';
import {
  hideMissionOverlayMissionListInModal,
  hideMissionOverlayMissionListModal,
  setMissionOverlayMissionsSortDirection,
  setMissionOverlayMissionsSortField,
  showMissionOverlayFlightLegListInModal,
  showMissionOverlayMissionListModal,
} from '../../../../../../store/ui/actions';
import { clearMissionData, setSelectedOverlaidMissionPlanId } from '../../../../../../store/flightLegs/actions';
import { removeBeamFromMap, clearMission, FTV_MAP_ID } from '../../../../../../store/maps/actions';
import { formatMissionPlanCreatedDate } from '../../../../../../utils/DateTimeUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../../../../utils/StringUtils';
import { getKuTerminalOnAircraft, getOtherTerminalOnAircraft } from '../../../../../../utils/TerminalUtilities';
import Scrollbars from '../../../../../common/layout/Scrollbars';
import appTheme from '../../../../../common/theme/appTheme.json';
import StyledProps from '../../../../../common/theme/AppThemeInterface';
import {
  StyledTableSortLabel,
  StyledArrowDownwardRounded,
  StyledPanelWrapper,
  StyledCloseRounded,
  StyledCircularProgress,
  StyledTableHeaderCell,
  StyledTableRow,
  StyledTableCell,
  StyledCheckRounded,
  StyledArrowForwardRounded,
  ErrorRequestingMissionsMessageContainer,
} from '../CommonStyledComponents';

export interface MissionPlanListContainerProps extends StyledProps {}

const MissionPlanListContainer = (props: MissionPlanListContainerProps) => {
  const dispatch = useDispatch();
  const missionPlans: MissionPlan[] = useSelector(selectMissionPlans, shallowEqual);
  const isRequestingMissionPlans = useSelector(selectLoadingMissionPlans, shallowEqual);
  const requestingMissionPlansError = useSelector(selectMissionPlansError, shallowEqual);
  const selectedOverlaidFlightLegId = useSelector(selectSelectedOverlaidFlightLegId(FTV_MAP_ID), shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const selectedOverlaidMissionPlanId = useSelector(selectselectedOverlaidMissionPlanId(FTV_MAP_ID), shallowEqual);
  const tailDetailsPanelTerminal = useSelector(selectTerminalDetails, shallowEqual);
  const selectedTerminalType = useSelector(selectTerminalType, shallowEqual);
  const flightLegServiceAlerts = useSelector(selectFlightLegServiceAlerts, shallowEqual);

  const missionOverlayMissionsSortField = useSelector(selectMissionOverlayMissionsSortField, shallowEqual);
  const missionOverlayMissionsSortDirection = useSelector(selectMissionOverlayMissionsSortDirection, shallowEqual);

  const fetchMissionPlans = useCallback(() => {
    if (!isNil(userId) && !isNil(tailDetailsPanelTerminal)) {
      let selectedDatapathUuid = tailDetailsPanelTerminal.datapathUuid;
      if (tailDetailsPanelTerminal.installationType === InstallationType.DUAL_TERMINAL) {
        const { terminalTypeLabel, datapathUuid } = tailDetailsPanelTerminal;
        const otherDatapathUuid = getOtherTerminalOnAircraft(tailDetailsPanelTerminal).datapathUuid;
        selectedDatapathUuid = selectedTerminalType === terminalTypeLabel ? datapathUuid : otherDatapathUuid;
      } else if (tailDetailsPanelTerminal.installationType === InstallationType.KU_PLUS_KA) {
        // MPT only stores the Ku terminal from a Ku + Ka terminal
        selectedDatapathUuid = getKuTerminalOnAircraft(tailDetailsPanelTerminal).datapathUuid;
      }
      dispatch(getMissionPlans(selectedDatapathUuid, userId));
    }
  }, [dispatch, userId, tailDetailsPanelTerminal, selectedTerminalType]);

  useEffect(() => {
    fetchMissionPlans();
  }, [fetchMissionPlans]);

  const handleMissionPlansModalCloseClicked = (item: any) => {
    // If a flight leg was not selected, remove the selected mission id
    if (isNil(selectedOverlaidFlightLegId)) {
      dispatch(hideMissionOverlayMissionListModal());
      dispatch(clearMission(FTV_MAP_ID)());
      dispatch(clearMissionData(FTV_MAP_ID)());
    }
    dispatch(hideMissionOverlayMissionListModal());
  };

  const handleMissionListRowClicked = (missionId: number, datapathUuid: string) => {
    if (!isNil(userId) && !isNil(tailDetailsPanelTerminal)) {
      if (missionId !== selectedOverlaidMissionPlanId) {
        if (
          !isNil(selectedOverlaidFlightLegId) &&
          !isNil(flightLegServiceAlerts) &&
          !isNil(flightLegServiceAlerts[selectedOverlaidFlightLegId]) &&
          size(flightLegServiceAlerts[selectedOverlaidFlightLegId].beams)
        ) {
          flightLegServiceAlerts[selectedOverlaidFlightLegId].beams.features.forEach((feature: Feature) => {
            dispatch(removeBeamFromMap(FTV_MAP_ID)(
              feature.properties?.name,
              feature.properties?.network, 
              feature.properties?.mapType
            ));
          });
        }
        dispatch(hideMissionOverlayMissionListModal());
        dispatch(clearMission(FTV_MAP_ID)());
        dispatch(clearMissionData(FTV_MAP_ID)());
      }
      dispatch(showMissionOverlayMissionListModal());
      dispatch(hideMissionOverlayMissionListInModal());
      dispatch(showMissionOverlayFlightLegListInModal());
      dispatch(setSelectedOverlaidMissionPlanId(FTV_MAP_ID)(missionId));
      dispatch(getFlightLegs(missionId, datapathUuid, userId));
    }
  };
  const getSortArrow = (columnTitle: React.ReactElement, columnSortField: keyof MissionPlan) => (
    <StyledTableSortLabel
      active={columnSortField === missionOverlayMissionsSortField}
      onClick={() => {
        if (columnSortField === missionOverlayMissionsSortField) {
          dispatch(
            setMissionOverlayMissionsSortDirection(missionOverlayMissionsSortDirection === 'asc' ? 'desc' : 'asc'),
          );
        } else {
          dispatch(setMissionOverlayMissionsSortField(columnSortField));
        }
      }}
      direction={missionOverlayMissionsSortDirection}
      IconComponent={StyledArrowDownwardRounded}
    >
      {columnTitle}
    </StyledTableSortLabel>
  );

  return (
    <StyledPanelWrapper>
      <Box
        display="flex"
        bgcolor={appTheme.primaryBackgroundColor}
        justifyContent="space-between"
        alignItems="center"
        pt="4px"
        pb="4px"
      >
        <Box color={`${appTheme.primaryTextColor}e1`} paddingLeft="16px" fontSize="15px" letterSpacing="0.14px">
          Overlay Mission
        </Box>
        <IconButton data-test="close_mission_plans_modal_button" onClick={handleMissionPlansModalCloseClicked}>
          <StyledCloseRounded />
        </IconButton>
      </Box>
      <Box>
        {isRequestingMissionPlans && !size(missionPlans) ? (
          <Box height="342px" paddingTop="12px" width="100%" display="flex" justifyContent="center" alignItems="center">
            <StyledCircularProgress />
          </Box>
        ) : size(missionPlans) ? (
          <Scrollbars autoHeight autoHeightMin={0} autoHeightMax="375px">
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <StyledTableHeaderCell>
                    <Box ml="16px">{getSortArrow(<>Title</>, 'name')}</Box>
                  </StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>Origin</>, 'origin')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>Legs</>, 'flightLegCount')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>Antenna</>, 'antennaType')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>HCX</>, 'useHcxBeams')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell>{getSortArrow(<>Created</>, 'creationDate')}</StyledTableHeaderCell>
                  <StyledTableHeaderCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {missionPlans
                  .slice()
                  .sort((a: MissionPlan, b: MissionPlan) => {
                    if (missionOverlayMissionsSortDirection === 'asc') {
                      return a[missionOverlayMissionsSortField] > b[missionOverlayMissionsSortField] ? 1 : -1;
                    } else {
                      return a[missionOverlayMissionsSortField] < b[missionOverlayMissionsSortField] ? 1 : -1;
                    }
                  })
                  .map((missionPlan: MissionPlan) => (
                    <StyledTableRow
                      data-test="mission_plan_overlay_list_table_row"
                      key={missionPlan.id}
                      onClick={() => {
                        handleMissionListRowClicked(missionPlan.id, missionPlan.datapathUuid);
                      }}
                    >
                      <StyledTableCell>
                        <Box marginLeft="16px" width="236px">
                          {missionPlan.name}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>{missionPlan.origin}</StyledTableCell>
                      <StyledTableCell>{missionPlan.flightLegCount}</StyledTableCell>

                      <StyledTableCell>
                        <Box width="150px">{missionPlan.antennaType}</Box>
                      </StyledTableCell>

                      <StyledTableCell>
                        {!isNil(missionPlan.useHcxBeams)
                          ? missionPlan.useHcxBeams
                            ? 'Yes'
                            : 'No'
                          : EMPTY_FIELD_STRING_PLACEHOLDER}
                      </StyledTableCell>
                      <StyledTableCell>
                        {missionPlan.creationDate
                          ? formatMissionPlanCreatedDate(missionPlan.creationDate)
                          : EMPTY_FIELD_STRING_PLACEHOLDER}
                      </StyledTableCell>
                      <StyledTableCell>
                        <IconButton data-test="row_forward_arrow_action" size="small">
                          {selectedOverlaidMissionPlanId === missionPlan.id ? (
                            <StyledCheckRounded />
                          ) : (
                            <StyledArrowForwardRounded />
                          )}
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
              </TableBody>
            </Table>
          </Scrollbars>
        ) : (
          <Box
            color={`${appTheme.primaryTextColor}cc`}
            textAlign="center"
            width="100%"
            pt="52px"
            letterSpacing="-0.14px"
            fontSize="12px"
          >
            (No Mission Plans Found)
          </Box>
        )}
      </Box>

      {!isNil(requestingMissionPlansError) && (
        <div className="loading__error__container">
          <div className="loading__error__message__container">
            <WarningRounded />
            <ErrorRequestingMissionsMessageContainer>
              Error Retrieving Mission Plans
            </ErrorRequestingMissionsMessageContainer>
            <Button variant="text" onClick={fetchMissionPlans}>
              Retry
            </Button>
          </div>
        </div>
      )}
    </StyledPanelWrapper>
  );
};

export default withTheme(MissionPlanListContainer);
